/**
 * Admin Arcs page.
 *
 * Refinement Phase A1 — first slice. Replaces the `Arcs (soon)`
 * placeholder in `Shell.tsx` with a real top-level admin surface.
 * Read-only dense table sourced from
 * `/v2/staging/arcs/admin/list.json`. Operators can still reach
 * the existing CP10 summary-edit flow via per-row links to
 * `/wiki/arcs/:id/edit`.
 *
 * Out of A1 scope (no UI here in this slice):
 *   - arc create form
 *   - arc full-row edit form (slug / title / type / status / dates /
 *     display_order / winner)
 *   - per-arc participations / challenges drill-in
 *   - filters, search, pagination
 *
 * Layout mirrors `apps/admin/src/pages/Entities.tsx` (CP17 Phase 2)
 * verbatim — same `<Shell>` + `FetchedArtifact` envelope, same
 * truncation-banner pattern, same dense-table classes.
 */

import type { ArcAdminListItem } from '@fishstank/contracts'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import { FetchedArtifact } from '../components/FetchedArtifact'
import { Shell } from '../layouts/Shell'
import { fetchArcsForAdmin } from '../lib/worker'

export function ArcsPage() {
  const query = useQuery({
    queryKey: ['arcs', 'admin', 'list'],
    queryFn: fetchArcsForAdmin,
  })

  const result = query.data
  const provenance =
    result && (result.kind === 'ok' || result.kind === 'empty')
      ? result.artifact.provenance
      : null

  return (
    <Shell provenance={provenance}>
      <header className="mb-6 flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-bold">Arcs</h1>
          <p className="mt-1 text-sm text-slate-400">
            Every arc in staging — seasons, specials, spinoffs, and any legacy
            off-season rows. Click "Edit summary" to open the existing summary
            editor; full-row arc editing lands in a later phase.
          </p>
        </div>
        <Link
          to="/arcs/new"
          data-testid="arcs-create-open"
          className="rounded-md bg-fishstank-accent px-3 py-1.5 text-sm font-semibold text-fishstank-ink hover:brightness-110"
        >
          + New arc
        </Link>
      </header>

      <FetchedArtifact
        result={result}
        isLoading={query.isLoading}
        testidPrefix="arcs"
        labels={{
          loading: 'Loading arcs…',
          errorTitle: "Couldn't load arcs.",
          empty: 'No arcs yet.',
        }}
      >
        {(artifact) => (
          <>
            {artifact.body.truncated && (
              <div
                data-testid="arcs-truncated-banner"
                className="mb-4 rounded border border-amber-700 bg-amber-950/40 p-3 text-sm text-amber-100"
              >
                Showing the first {artifact.body.arcs.length} arcs; more are
                present. A search affordance will land in a future phase.
              </div>
            )}
            <AdminArcsTable arcs={artifact.body.arcs} />
          </>
        )}
      </FetchedArtifact>
    </Shell>
  )
}

interface AdminArcsTableProps {
  arcs: readonly ArcAdminListItem[]
}

function AdminArcsTable({ arcs }: AdminArcsTableProps) {
  return (
    <div className="overflow-x-auto rounded-md border border-slate-800">
      <table
        data-testid="arcs-admin-table"
        data-arc-count={arcs.length}
        className="min-w-full divide-y divide-slate-800 text-sm"
      >
        <thead className="bg-fishstank-surface text-xs uppercase tracking-wide text-slate-400">
          <tr>
            <th className="px-3 py-2 text-left font-medium">Title</th>
            <th className="px-3 py-2 text-left font-medium">Slug</th>
            <th className="px-3 py-2 text-left font-medium">Type</th>
            <th className="px-3 py-2 text-left font-medium">Status</th>
            <th className="px-3 py-2 text-left font-medium">Dates</th>
            <th className="px-3 py-2 text-right font-medium">Order</th>
            <th className="px-3 py-2 text-right font-medium">Participants</th>
            <th className="px-3 py-2 text-right font-medium">Challenges</th>
            <th className="px-3 py-2 text-left font-medium">Updated</th>
            <th className="px-3 py-2 text-right font-medium">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-900 bg-fishstank-ink/40">
          {arcs.map((arc) => (
            <AdminArcRow key={arc.id} arc={arc} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

interface AdminArcRowProps {
  arc: ArcAdminListItem
}

/**
 * Pretty-print the arc_type column. Surfaces 'off_season' as
 * "Off-season (legacy)" so the operator can spot historical rows
 * that the create form will not propagate forward.
 */
function arcTypeLabel(arcType: ArcAdminListItem['arc_type']): string {
  switch (arcType) {
    case 'season':
      return 'Season'
    case 'special':
      return 'Special'
    case 'spinoff':
      return 'Spinoff'
    case 'off_season':
      return 'Off-season (legacy)'
    default:
      return arcType
  }
}

/**
 * Status chip color. Active arcs render in emerald to match the
 * single-active-arc invariant; everything else is neutral slate.
 */
function statusChipClass(status: ArcAdminListItem['status']): string {
  if (status === 'active') {
    return 'inline-block rounded-full bg-emerald-900/50 px-2 py-0.5 text-xs font-medium text-emerald-200'
  }
  return 'inline-block rounded-full bg-slate-800 px-2 py-0.5 text-xs font-medium text-slate-300'
}

function formatDateRange(
  start: string | null,
  end: string | null,
): string {
  if (start === null && end === null) return '—'
  const left = start ?? '?'
  const right = end ?? 'present'
  return `${left} – ${right}`
}

function AdminArcRow({ arc }: AdminArcRowProps) {
  return (
    <tr data-testid={`arcs-admin-row-${arc.id}`} data-arc-slug={arc.slug}>
      <td className="px-3 py-2 font-semibold text-slate-100">{arc.title}</td>
      <td className="px-3 py-2 font-mono text-xs text-slate-300">{arc.slug}</td>
      <td className="px-3 py-2 text-slate-300" data-arc-type={arc.arc_type}>
        {arcTypeLabel(arc.arc_type)}
      </td>
      <td className="px-3 py-2" data-arc-status={arc.status}>
        <span className={statusChipClass(arc.status)}>{arc.status}</span>
      </td>
      <td className="px-3 py-2 text-xs text-slate-400">
        {formatDateRange(arc.start_date, arc.end_date)}
      </td>
      <td className="px-3 py-2 text-right text-slate-300">
        {arc.display_order ?? '—'}
      </td>
      <td className="px-3 py-2 text-right text-slate-300">
        {arc.participation_count}
      </td>
      <td className="px-3 py-2 text-right text-slate-300">
        {arc.challenge_count}
      </td>
      <td className="px-3 py-2 text-xs text-slate-500">
        {arc.updated_at ? arc.updated_at.slice(0, 10) : '—'}
      </td>
      <td className="px-3 py-2 text-right">
        <Link
          to={`/arcs/${arc.id}/edit`}
          data-testid={`arcs-admin-edit-link-${arc.id}`}
          className="mr-3 text-fishstank-accent underline-offset-2 hover:underline"
        >
          Edit
        </Link>
        <Link
          to={`/wiki/arcs/${arc.id}/edit`}
          data-testid={`arcs-admin-edit-summary-link-${arc.id}`}
          className="text-fishstank-accent underline-offset-2 hover:underline"
        >
          Edit summary
        </Link>
      </td>
    </tr>
  )
}
